import { usePlayer, usePlayerActions } from "@utils/player/core/usePlayer";
import React, { useEffect, useRef, useCallback } from "react";
import { ActivityIndicator, Platform, StyleSheet } from "react-native";
import { BlurView } from "expo-blur";
import { LinearGradient } from "expo-linear-gradient";
import Animated, { FadeInDown, FadeOut } from "react-native-reanimated";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import { useTabBarHeight } from "@hooks/useTabBarHeight";
import config from "@utils/config";
import { useCompleteWelcome } from "@hooks/trpc/useOnboardingMutations";

import {
  selectActiveSong,
  selectActiveSongId,
} from "@utils/player/core/selectors";
import { useMe } from "@hooks/trpc/useMe";
import { useOnboardingStore } from "./store/onboardingStore";
import { trackOnb } from "./analytics/trackOnboarding";
import { router } from "expo-router";
import { fonts } from "@theme";
import type { StreamingService } from "./constants";

const SERVICE_DISPLAY_NAMES: Record<StreamingService, string> = {
  spotify: "Spotify",
  appleMusic: "Apple Music",
  youtube: "YouTube",
  soundCloud: "SoundCloud",
};

type WelcomePhase = "play" | "impact";

export function WelcomeOverlay() {
  const playerActions = usePlayerActions();

  const [phase, setPhase] = React.useState<WelcomePhase>("play");
  const { mutate: completeWelcome } = useCompleteWelcome();
  const hasTrackedShow = useRef(false);
  // Welcome completes once: the song-change effect below runs again on every
  // render that keeps the changed song, and a tap can follow it.
  const completed = useRef(false);

  const activeSong = usePlayer(selectActiveSong);
  const activeSongId = usePlayer(selectActiveSongId);
  const { query: meQuery } = useMe();
  const onboardingStep = meQuery.data?.onboardingStep;
  const hasImports = onboardingStep === "welcome_import_pending";
  const streamingService = useOnboardingStore((s) => s.streamingService);

  // Snapshot song data so text doesn't flash when the song changes during dismiss.
  // Capture the first non-null activeSong and keep it stable thereafter. Using
  // the functional initializer + key-style latch via useMemo keeps the value
  // stable without writing a ref during render.
  const [displaySong, setDisplaySong] = React.useState(activeSong);
  if (activeSong && !displaySong) {
    // Adjusting state during render is the recommended pattern for deriving a
    // latched value from a changing prop (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    setDisplaySong(activeSong);
  }
  const artistName =
    displaySong?.artists?.map((a) => a.name).join(", ") ?? null;

  // Track show event once
  useEffect(() => {
    if (!hasTrackedShow.current) {
      hasTrackedShow.current = true;
      trackOnb("signup", "welcome-play", "show");
    }
  }, []);

  // Track the song ID when impact phase begins, so we can detect changes
  const impactSongIdRef = useRef<string | null>(null);

  const handleComplete = useCallback(() => {
    if (completed.current) return;
    completed.current = true;
    trackOnb("signup", "welcome-impact", "complete");
    completeWelcome();
  }, [completeWelcome]);

  const handlePlay = useCallback(() => {
    trackOnb("signup", "welcome-play", "play-started");
    impactSongIdRef.current = activeSongId;
    setPhase("impact");
    playerActions.play().catch(console.error);
  }, [activeSongId]);

  // Complete welcome when song changes (swipe or auto-advance) during impact phase
  useEffect(() => {
    if (
      phase === "impact" &&
      impactSongIdRef.current &&
      activeSongId &&
      activeSongId !== impactSongIdRef.current
    ) {
      handleComplete();
    }
  }, [phase, activeSongId, handleComplete]);

  const handleProfileTap = useCallback(() => {
    handleComplete();
    router.push("/(app)/(tabs)/profile");
  }, [handleComplete]);

  const serviceName = streamingService
    ? SERVICE_DISPLAY_NAMES[streamingService]
    : "your library";

  // Import loading state: hasImports but queue isn't ready yet.
  const isImportLoading = hasImports && !displaySong;

  // Build heading text using snapshotted song so it doesn't flash on song change
  const headingText = isImportLoading
    ? `We're importing your playlists from ${serviceName}...`
    : displaySong
      ? hasImports
        ? `We just imported "${displaySong.title}" into your library`
        : `You might like "${displaySong.title}"${artistName ? ` by ${artistName}` : ""}`
      : "Tap to play your first song";

  const subtitleText = isImportLoading
    ? "This will only take a moment"
    : displaySong
      ? "Try to play it now"
      : hasImports
        ? "We found music you love"
        : "We picked something you'll enjoy";

  if (phase === "play") {
    return (
      <WelcomeContainer
        testID="welcome-overlay-play"
        touchable
        onPress={isImportLoading ? undefined : handlePlay}
      >
        {/* Text above center */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(200)}
          style={styles.textAboveCenter}
        >
          <LText
            fontSize={24}
            fontFamily={fonts.semiBold}
            color="white"
            textAlign="center"
            lineHeight={24}
            style={{
              letterSpacing: -0.48,
              textShadowColor: "rgba(0,0,0,0.8)",
              textShadowOffset: { width: 0, height: 2 },
              textShadowRadius: 8,
            }}
          >
            {headingText}
          </LText>
          <LBox mt="s2">
            <LText
              fontSize={14}
              fontFamily={fonts.regular}
              color="whiteTranslucent60"
              textAlign="center"
              lineHeight={18}
              style={{
                letterSpacing: -0.28,
                textShadowColor: "rgba(0,0,0,0.8)",
                textShadowOffset: { width: 0, height: 1 },
                textShadowRadius: 6,
              }}
            >
              {subtitleText}
            </LText>
          </LBox>
        </Animated.View>

        {/* Play button or loading indicator at center */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(400)}
          style={styles.playCenter}
        >
          {isImportLoading ? (
            <ActivityIndicator size="large" color="white" />
          ) : (
            <LPressable
              fadeEnabled
              sentryEventName="welcome_play_song"
              onPress={handlePlay}
            >
              <LBox
                width={80}
                height={80}
                borderRadius="max"
                backgroundColor="whiteTranslucent20"
                alignItems="center"
                justifyContent="center"
              >
                <LIcon name="play" size={36} stroke="white" fill="white" />
              </LBox>
            </LPressable>
          )}
        </Animated.View>

        {/* Bottom spacer to balance the text above */}
        <LBox flex={1} />
      </WelcomeContainer>
    );
  }

  return (
    <ImpactOverlay
      onProfileTap={handleProfileTap}
      onComplete={handleComplete}
    />
  );
}

function ImpactOverlay({
  onProfileTap,
  onComplete,
}: {
  onProfileTap: () => void;
  onComplete: () => void;
}) {
  const tabBarHeight = useTabBarHeight();

  return (
    <LBox
      testID="welcome-overlay-impact"
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      zIndex={1000}
      pointerEvents="box-none"
    >
      {/* Bottom gradient — matches BringYourMusic */}
      <LinearGradient
        colors={["transparent", "rgba(0,0,0,0.85)", "#000"]}
        locations={[0, 0.8, 1]}
        style={{
          position: "absolute",
          bottom: tabBarHeight + config.UI.barMusicPlayerHeight,
          left: 0,
          right: 0,
          height: "45%",
        }}
        pointerEvents="none"
      />
      {/* Black fill behind tab bar + bar music player */}
      <LBox
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        style={{
          height: tabBarHeight + config.UI.barMusicPlayerHeight,
          backgroundColor: "#000",
        }}
      />

      {/* Content pinned to bottom */}
      <LBox
        flex={1}
        justifyContent="flex-end"
        px="m1"
        style={{
          paddingBottom: tabBarHeight + config.UI.barMusicPlayerHeight + 32,
        }}
        pointerEvents="box-none"
      >
        <LBox gap="s3" mb="m1" entering={FadeInDown.duration(400).delay(200)}>
          <LText
            fontSize={32}
            fontFamily={fonts.semiBold}
            color="white"
            style={{
              letterSpacing: -0.64,
              textShadowColor: "rgba(0,0,0,0.8)",
              textShadowOffset: { width: 0, height: 2 },
              textShadowRadius: 8,
            }}
            lineHeight={32}
          >
            {"Check your impact in\nyour profile"}
          </LText>
        </LBox>

        <LBox
          alignItems="center"
          mb="m1"
          entering={FadeInDown.duration(400).delay(400)}
        >
          <LPressable
            fadeEnabled
            sentryEventName="welcome_check_impact"
            onPress={onProfileTap}
          >
            <BlurView
              intensity={30}
              tint="light"
              style={{
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 16,
                overflow: "hidden",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowRadius: 12,
                shadowOpacity: 0.6,
                elevation: 8,
              }}
            >
              <LText
                fontSize={16}
                fontFamily={fonts.medium}
                color="white"
                style={{ letterSpacing: -0.32 }}
                lineHeight={21}
              >
                Go to your Profile
              </LText>
            </BlurView>
          </LPressable>
        </LBox>

        <LBox
          alignItems="center"
          entering={FadeInDown.duration(400).delay(600)}
        >
          <LPressable
            fadeEnabled
            sentryEventName="welcome_skip_impact"
            onPress={onComplete}
          >
            <LText
              fontSize={16}
              fontFamily={fonts.medium}
              style={{ color: "#C4C4C4", letterSpacing: -0.32 }}
              lineHeight={20}
              textAlign="center"
            >
              Or scroll down to play more music now
            </LText>
          </LPressable>
        </LBox>
      </LBox>
    </LBox>
  );
}

function WelcomeContainer({
  children,
  testID,
  touchable,
  onPress,
}: {
  children: React.ReactNode;
  testID?: string;
  touchable?: boolean;
  onPress?: () => void;
}) {
  const pe = touchable ? "auto" : "none";
  return (
    <LBox
      testID={testID}
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      zIndex={1000}
      pointerEvents={pe}
      exiting={FadeOut.duration(400)}
    >
      {Platform.OS === "android" ? (
        <LinearGradient
          colors={["rgba(0,0,0,0.7)", "rgba(0,0,0,0.5)", "rgba(0,0,0,0.7)"]}
          locations={[0, 0.4, 1]}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
      ) : (
        <BlurView
          intensity={40}
          tint="dark"
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
      )}
      {onPress ? (
        <LPressable
          onPress={onPress}
          sentryEventName={null}
          style={{ flex: 1 }}
        >
          {children}
        </LPressable>
      ) : (
        <LBox flex={1} pointerEvents={pe}>
          {children}
        </LBox>
      )}
    </LBox>
  );
}

const styles = StyleSheet.create({
  textAboveCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "flex-end",
    paddingHorizontal: 32,
    paddingBottom: 24,
  },
  playCenter: {
    alignItems: "center",
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 32,
  },
});
